/**
 * The creator's shortcuts: what to pick for somebody, and how to find a thing
 * without reading the whole catalogue.
 *
 * The owner's complaint about `/pl/kreator`, in full: *„mało intuicyjna, sporo
 * wybierania z listy, mało automatyzacji i szybkich rozwiązań"*. All three had
 * one root. Everything the site sells that was not one of three featured rows
 * was reachable only through a `<select>` — twenty-five options in a dropdown,
 * grouped by page, with no prices and no search. Finding the Sony A7 III meant
 * opening a list and reading it.
 *
 * And every position it produced arrived EMPTY: `emptyPick` leaves
 * `variantSlug` at `null` unless the offering happens to have exactly one
 * variant, so adding a montage put an unpriced card on the screen asking which
 * of three levels you meant, before you had said anything else about it.
 *
 * So: search that tolerates how Polish is actually typed, a recommended level
 * chosen for you, and a few whole baskets in one click.
 *
 * Pure and in `lib/` because this app's `vitest` runs in `node` with no JSX
 * transform — a rule about what gets added to somebody's basket has to be
 * testable, and one living in a `.client.tsx` is not.
 */

import { type CreatorOfferingView, type CreatorPick, defaultQtyFor, emptyPick } from '@/lib/creator'
import { formatPlnMinor } from '@/lib/money'
import { shootFloorNetMinor } from '@/lib/order-money'

/* ── WHICH LEVEL TO PICK FOR SOMEBODY ──────────────────────── */

/**
 * The level a one-click add lands on.
 *
 * The one flagged `popular` — the same "Polecany" the offer pages print — then
 * the cheapest thing anybody can actually be charged for, then whatever is
 * first. The fallbacks matter: an offering whose panel editor never ticked
 * "polecany" must still add as a PRICED line, because the whole point is that
 * the card arrives with a number on it.
 *
 * Never a quote-only variant while a priced one exists. "Wycena indywidualna"
 * as the default of a quick add would put a position in the basket that the
 * checkout cannot total.
 */
export function recommendedVariant(offering: CreatorOfferingView) {
  const variants = offering.variants
  if (variants.length === 0) return undefined

  const popular = variants.find((v) => v.popular && !v.quoteOnly)
  if (popular) return popular

  const priced = variants.filter((v) => !v.quoteOnly && v.priceNet > 0)
  const cheapest = priced.reduce<(typeof priced)[number] | undefined>(
    (best, v) => (best === undefined || v.priceNet < best.priceNet ? v : best),
    undefined,
  )
  return cheapest ?? variants[0]
}

/**
 * A position filled in as far as it honestly can be.
 *
 * Level and quantity, and nothing else. A DATE is deliberately left empty even
 * where the offering needs one: guessing puts a camera on a van on a day
 * nobody chose, and the customer would have to notice that we picked it before
 * they could correct it. The card asks, which is the only honest default for a
 * booking.
 *
 * Everything `emptyPick` already decides — the single city, the single
 * variant, the film length band — is kept as it is. This adds the one decision
 * the picker used to leave on the table.
 */
export function quickPick(offering: CreatorOfferingView): CreatorPick {
  const base = emptyPick(offering)
  if (base.variantSlug !== null) return base

  const pick = recommendedVariant(offering)
  if (!pick) return base
  return { ...base, variantSlug: pick.slug, qty: defaultQtyFor(offering, pick) }
}

/** What a tile prints under its name: the entry price for the whole offering. */
export function fromPrice(offering: CreatorOfferingView): string | null {
  const priced = offering.variants.filter((v) => !v.quoteOnly && v.priceNet > 0)
  if (priced.length === 0) {
    // A shoot day has no catalogue price and is not a quote either — the
    // session model prices it, and this is its floor. Same figure the role card
    // on `/pl/fotograf-operator` shows, so the two doors to the same offering
    // cannot name different numbers.
    const floor = shootFloorNetMinor(offering)
    // No „od" of its own: the tile prints one before whatever this returns,
    // exactly as it does for a priced offering.
    return floor === null ? null : `${formatPlnMinor(floor)}${unitSuffix(offering)}`
  }

  const cheapest = priced.reduce((best, v) => (v.priceNet < best.priceNet ? v : best))
  return `${cheapest.price}${cheapest.suffix ? ` ${cheapest.suffix}` : ''}`
}

/**
 * ` / dzień` — the level's own suffix, not the offering's unit.
 *
 * The same string a priced tile would have printed, taken off the first level
 * rather than rebuilt from `unitForms`: „dzień zdjęciowy" is the unit a
 * quantity is counted in, and the catalogue already decided the shorter form
 * belongs under a price.
 */
function unitSuffix(offering: CreatorOfferingView): string {
  const suffix = offering.variants[0]?.suffix
  return suffix ? ` ${suffix}` : ''
}

/* ── FINDING SOMETHING ─────────────────────────────────────── */

/**
 * Fold a string down to what a search should actually compare.
 *
 * Diacritics come off, because the one thing certain about somebody typing
 * "swiatlo" into a search box on a phone is that they will not reach for ś.
 * `NFD` splits a letter from its mark and the class removes the mark, so this
 * needs no table for ą, ć, ę, ń, ó, ś, ź or ż.
 *
 * Ł IS THE EXCEPTION, and it took a failing test to notice. U+0142 is a letter
 * with a stroke THROUGH it, not a letter carrying a combining mark, so `NFD`
 * leaves it exactly as it is and `\p{Diacritic}` has nothing to remove — which
 * meant "Światło" folded to "swiatło" and the query "swiatlo" matched nothing
 * at all. It is the one Polish letter that needs saying out loud, and it is
 * also the one in `Światło`, `Dźwięk` and half the words on this site.
 */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[łŁ]/g, 'l')
    .toLowerCase()
    .trim()
}

/**
 * Does this offering answer that query?
 *
 * Name, blurb, the page it is sold on AND its own shelf, so "wynajem" finds
 * every camera, "swiatlo" finds the lamp and "youtube" finds the consultation
 * whose blurb says so. The shelf is the one that earns its place: eleven hire
 * items share the page label "Wynajem", and without `categoryLabel` the words
 * somebody actually types for them matched nothing.
 *
 * EVERY word has to match somewhere — "sony obiektyw" should narrow, not
 * widen, which is what an any-word match would do.
 */
export function matchesQuery(offering: CreatorOfferingView, query: string): boolean {
  const needles = fold(query).split(/\s+/).filter(Boolean)
  if (needles.length === 0) return true

  const haystack = fold(
    `${offering.name} ${offering.blurb} ${offering.pageLabel} ${offering.categoryLabel}`,
  )
  return needles.every((needle) => haystack.includes(needle))
}

/** The page tabs above the grid, in catalogue order, each with its count. */
export function catalogueGroups(
  offerings: readonly CreatorOfferingView[],
): { slug: string; label: string; count: number }[] {
  const groups = new Map<string, { slug: string; label: string; count: number }>()
  for (const offering of offerings) {
    const row = groups.get(offering.pageSlug)
    if (row) row.count += 1
    else
      groups.set(offering.pageSlug, {
        slug: offering.pageSlug,
        label: offering.pageLabel,
        count: 1,
      })
  }
  return [...groups.values()]
}

/* ── WHOLE BASKETS, IN ONE CLICK ───────────────────────────── */

/**
 * A ready-made order for the three things people most often come here to buy.
 *
 * Written as offering slugs and quantities, NOT as variant slugs: the levels
 * are named differently per offering (`basic`/`creator`/`pro` on miniaturki,
 * `starter`/`creator`/`pro` on rolki) and a preset naming one directly would
 * break the day somebody renames a level in the panel. {@link quickPick}
 * chooses, the same way a single tile does.
 *
 * Anything whose slug is not in the live catalogue is DROPPED rather than
 * failing the preset — an offering can be retired in the panel and a shortcut
 * here must degrade to the rest of itself, never to an error.
 *
 * These start an order, they do not finish one. Every line still asks for its
 * brief and its dates on its own card, which is the part no shortcut should
 * answer on somebody's behalf.
 */
export type QuickStart = {
  id: string
  label: string
  blurb: string
  items: { offeringSlug: string; qty?: number }[]
}

export const QUICK_STARTS: readonly QuickStart[] = [
  {
    id: 'kanal',
    label: 'Kanał na start',
    // „Film" as a product went out of the catalogue on 2026-09-19 — the owner:
    // „Nie ma już czegoś takiego jak montaż, jest taka zakładka ale usługi już
    // nie ma." The shortcut kept naming it, and `resolveQuickStart` quietly
    // dropped the line it could not resolve: the tile went on promising „Film z
    // miniaturkami" and delivered three thumbnails. Podcast is what the montage
    // page now sells an episode as, and it is the row `/pl/montaz` quotes its
    // „od …" from.
    blurb: 'Odcinek z miniaturkami — komplet na jedno wydanie.',
    items: [
      { offeringSlug: 'montaz-podcast', qty: 1 },
      { offeringSlug: 'miniaturka', qty: 3 },
    ],
  },
  {
    id: 'rolki',
    label: 'Seria rolek',
    blurb: 'Dziesięć pionowych formatów na miesiąc publikacji.',
    items: [{ offeringSlug: 'rolka', qty: 10 }],
  },
  {
    id: 'miniatury',
    label: 'Paczka miniaturek',
    blurb: 'Pięć miniaturek — najtańsza sztuka przy większej paczce.',
    items: [{ offeringSlug: 'miniaturka', qty: 5 }],
  },
]

/** Turn a shortcut into the positions it stands for, against the live offer. */
export function resolveQuickStart(
  offerings: readonly CreatorOfferingView[],
  start: QuickStart,
): { offering: CreatorOfferingView; pick: CreatorPick }[] {
  return start.items.flatMap((item) => {
    const offering = offerings.find((o) => o.slug === item.offeringSlug)
    if (!offering) return []

    const pick = quickPick(offering)
    return [{ offering, pick: item.qty === undefined ? pick : { ...pick, qty: item.qty } }]
  })
}

/** Shortcuts this catalogue can actually deliver in full. */
export function availableQuickStarts(
  offerings: readonly CreatorOfferingView[],
): { start: QuickStart; lines: { offering: CreatorOfferingView; pick: CreatorPick }[] }[] {
  return QUICK_STARTS.flatMap((start) => {
    const lines = resolveQuickStart(offerings, start)
    // A shortcut that resolves to nothing is not a shortcut. One that lost a
    // line still saves the picking for the rest, so it stays.
    return lines.length === 0 ? [] : [{ start, lines }]
  })
}
